import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { usersService } from '../../services/users';

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await usersService.getUsers();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users');
    }
  }
);

export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await usersService.createUser(userData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create user');
    }
  }
);

export const fetchStates = createAsyncThunk(
  'users/fetchStates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await usersService.getStates();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch states');
    }
  }
);

export const fetchDiseases = createAsyncThunk(
  'users/fetchDiseases',
  async (_, { rejectWithValue }) => {
    try {
      const response = await usersService.getDiseases();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch diseases');
    }
  }
);

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const response = await usersService.updateUser(id, userData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update user');
    }
  }
);

export const partialUpdateUser = createAsyncThunk(
  'users/partialUpdateUser',
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const response = await usersService.partialUpdateUser(id, userData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update user');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (id, { rejectWithValue }) => {
    try {
      await usersService.deleteUser(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete user');
    }
  }
);

export const fetchTeamHierarchy = createAsyncThunk(
  'users/fetchTeamHierarchy',
  async (_, { rejectWithValue }) => {
    try {
      const response = await usersService.getTeamHierarchy();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch team hierarchy');
    }
  }
);

export const fetchAvailableTeamLeads = createAsyncThunk(
  'users/fetchAvailableTeamLeads',
  async (_, { rejectWithValue }) => {
    try {
      const response = await usersService.getAvailableTeamLeads();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch available team leads');
    }
  }
);

export const fetchAvailableAssistantManagers = createAsyncThunk(
  'users/fetchAvailableAssistantManagers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await usersService.getAvailableAssistantManagers();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch available assistant managers');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState: {
    users: [],
    states: [],
    diseases: [],
    teamHierarchy: null,
    availableTeamLeads: [],
    availableAssistantManagers: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        // Handle paginated response with results array
        if (action.payload && action.payload.results) {
          state.users = Array.isArray(action.payload.results) ? action.payload.results : [];
        } else {
          state.users = Array.isArray(action.payload) ? action.payload : [];
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.users.unshift(action.payload);
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        // Handle paginated response with results array
        if (action.payload && action.payload.results) {
          state.states = Array.isArray(action.payload.results) ? action.payload.results : [];
        } else {
          state.states = Array.isArray(action.payload) ? action.payload : [];
        }
      })
      .addCase(fetchDiseases.fulfilled, (state, action) => {
        // Handle paginated response with results array
        if (action.payload && action.payload.results) {
          state.diseases = Array.isArray(action.payload.results) ? action.payload.results : [];
        } else {
          state.diseases = Array.isArray(action.payload) ? action.payload : [];
        }
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(partialUpdateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter(user => user.id !== action.payload);
      })
      .addCase(fetchTeamHierarchy.fulfilled, (state, action) => {
        state.teamHierarchy = action.payload;
      })
      .addCase(fetchAvailableTeamLeads.fulfilled, (state, action) => {
        state.availableTeamLeads = action.payload.team_leads || [];
      })
      .addCase(fetchAvailableAssistantManagers.fulfilled, (state, action) => {
        state.availableAssistantManagers = action.payload.assistant_managers || [];
      });
  },
});

export const { clearError } = usersSlice.actions;
export default usersSlice.reducer;